<template>
  <div class="news-board">
    <transition-group name="fade-slide" tag="div" class="news-card-list">
      <div
        v-for="(item, index) in all_msg"
        :key="item.id ?? index"
        class="news-card"
      >
        <div class="news-body">
          <!-- 时间戳 -->
          <div
            class="news-time-stamp"
            :data-date="item.time.split(' ')[0]"
          >
            <span class="news-time">{{ item.time.split(' ')[1] }}</span>
          </div>

          <!-- 内容区 -->
          <div class="news-content-wrap">
            <div class="news-msg">
              {{ item.msg }}
              <span class="news-from">//{{ item.from }}//</span>
            </div>
          </div>
        </div>
      </div>
    </transition-group>

    <!-- 静默失败提示：失败时不弹窗，只在页面内提示并自动重试 -->
    <div v-if="errHint" class="news-err-hint">
      <span>{{ errHint }}</span>
      <button class="news-retry-btn" @click="getNews">重试</button>
    </div>
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, ref } from 'vue'
import http from '@/api/http'
import { ElMessage } from 'element-plus'

/* 数据 */
const all_msg = ref([])
/* 页面内的静默失败提示，替代每次失败都弹窗 */
const errHint = ref('')

/* 轮询参数 */
const POLL_INTERVAL = 60 * 1000   // 成功后的正常轮询间隔
const RETRY_INTERVAL = 15 * 1000  // 失败后的快速重试间隔
const POPUP_AFTER = 3             // 连续失败多少次才弹一次窗提醒

let timer = null
let inFlight = false              // 防止上一个请求未完成时重复发起
let failCount = 0

onMounted(() => {
  document.addEventListener('visibilitychange', handleVisibility)
  getNews()
})

onUnmounted(() => {
  clearTimeout(timer)
  document.removeEventListener('visibilitychange', handleVisibility)
})

/* 页面隐藏时暂停轮询，回到前台立即刷新 */
function handleVisibility() {
  if (document.hidden) {
    clearTimeout(timer)
    timer = null
  } else if (!timer) {
    getNews()
  }
}

/* 拉取新闻（自调度轮询：成功 60s 后再查，失败 15s 后重试） */
function getNews() {
  if (inFlight) return
  inFlight = true

  // 新闻响应体较大（约 47KB 压缩后），服务器出口带宽紧张时传输需数秒，
  // 单独放宽本次请求的超时时间，避免误判为超时
  http.get('/api/news', {}, { timeout: 30 * 1000 })
    .then(res => {
      const resp = res.data
      if (resp.result !== 'ok') {
        handleFailure(resp.msg || '服务异常')
        return
      }

      let list
      try {
        // 后端返回的是 JSON 字符串
        list = JSON.parse(resp.msg)
      } catch (e) {
        // 数据解析失败与网络无关，单独处理，避免误报为"网络异常"
        console.error('新闻数据解析失败:', e)
        handleFailure('数据解析失败')
        return
      }

      all_msg.value = list
      failCount = 0
      errHint.value = ''
    })
    .catch(err => handleFailure(readError(err)))
    .finally(() => {
      inFlight = false
      scheduleNext()
    })
}

/* 失败处理：页面内静默提示并自动重试，连续失败多次才弹一次窗 */
function handleFailure(msg) {
  failCount++
  errHint.value = `${msg}，自动重试中…`
  if (failCount === POPUP_AFTER || (failCount > POPUP_AFTER && failCount % 10 === 0)) {
    ElMessage.error(msg)
  }
}

/* 区分超时 / HTTP 错误 / 网络错误，HTTP 错误优先展示后端给出的原因 */
function readError(err) {
  if (err.code === 'ECONNABORTED') return '请求超时'
  const serverMsg = err.response?.data?.msg
  if (serverMsg) return serverMsg
  if (err.response) return `请求失败(${err.response.status})`
  return '网络异常'
}

/* 成功按正常间隔轮询，失败后缩短间隔快速重试；页面隐藏时不调度 */
function scheduleNext() {
  clearTimeout(timer)
  timer = null
  if (!document.hidden) {
    timer = setTimeout(getNews, failCount > 0 ? RETRY_INTERVAL : POLL_INTERVAL)
  }
}
</script>

<style scoped>
/* ---------- 整体面板 ---------- */
.news-board {
  height: 100%;      /* 现在父级(.main)高度已确定，100% 有效 */
  overflow-y: auto;  /* 内容超出自己滚 */
  padding: 8px;      /* 原来 margin 会溢出，换成 padding 更保险 */
  box-sizing: border-box;
}

/* ---------- 卡片列表 ---------- */
.news-card-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* ---------- 单张卡片 ---------- */
.news-card {
  background: var(--background-color);
  border-radius: 12px;
  box-shadow: var(--box-shadow);
  overflow: hidden;          /* 圆角裁剪内部溢出 */
  padding: 10px;
}

/* hover 上浮+阴影加深 */
.news-card:hover {
  transform: translateY(-4px);
}

/* ---------- 卡片内部 ---------- */
.news-body {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.news-time-stamp {
  flex: 0 0 100px;
  display: flex;
  flex-direction: column;
  align-items: center;      /* 垂直居中 */
  background: var(--time-bg-color);
  color: var(--color);
  border-radius: 6px;
}

/* 第一行：日期 */
.news-time-stamp::before {
  content: attr(data-date);
  font-weight: 600;
  font-size: 14px;
}

/* 第二行：时间 */
.news-time-stamp .time {
  font-size: 13px;
  margin-top: 2px;            /* 两行之间一点间距 */
}

.news-content-wrap {
  flex: 1;
  display: flex;
  align-items: center;      /* 垂直居中 */
}

.news-msg {
  font-size: 15px;
  color: var(--color);
  margin-bottom: 4px;
  align-items: center;      /* 垂直居中 */
}

.news-from {
  font-size: 13px;
  color: var(--water-black);
}

/* ---------- 静默失败提示 ---------- */
.news-err-hint {
  margin-top: 10px;
  padding: 8px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 13px;
  color: var(--color);
  background: var(--background-color);
  border-radius: 8px;
  box-shadow: var(--box-shadow);
}

.news-retry-btn {
  border: 1px solid var(--color);
  background: transparent;
  color: var(--color);
  padding: 2px 10px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
}

.news-retry-btn:hover { opacity: .8; }
</style>